import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';

import {
  TextButtonRegularSmall as TextButton,
  PrimaryButtonMedium as PrimaryButton,
  SecondaryButtonMedium as SecondaryButton,
} from '@xingternal/malt-button';
import UserImage from '@xingternal/malt-user-image';
import { H } from '@xingternal/malt-typography';
import {
  useMediaListener,
  BREAKPOINTS,
} from '@xingternal/util/withMediaListener';

import StretchButtonGroup from './../../Components/StretchButtonGroup/StretchButtonGroup';
import PasswordField from './../../Components/PasswordField/PasswordField';
import FormMessage from './../../Components/FormMessage/FormMessage';
import CenterImage from './../../Components/CenterImage/CenterImage';
import { CustomH, CustomP } from './../../Components/CustomMalt/Typography';
import Spacer from './../../Components/Spacer/Spacer';

import styles from './LoginKnownUser.css';
import messages from './messages';

const getSalutationKey = gender =>
  gender === 'm' ? messages.salutationMale : messages.salutationFemale;

const LoginKnownUser = ({
  handleChange,
  handleSubmit,
  oldLoginAction,
  forgotPasswordAction,
  oneTimeLinkAction,
  isSubmitting,
  errors,
  passwordRef,
  user: { email, lastName, firstName, gender, imageUrl },
  formMessage: { notice, error },
  oneTimeLinkError,
  didResetPassword,
  setErrors,
}) => {
  useEffect(() => {
    if (error) {
      setErrors({ credentials: error });
    }
  }, []);
  const breakpoint = useMediaListener();

  return (
    <>
      <CenterImage
        image={
          <UserImage
            userName={email}
            src={`${imageUrl}.64x64.jpg`}
            size={'300'}
            gender={gender}
            className={styles.userImage}
          />
        }
      />
      <div data-qa={'malt-login-known-user-headline'}>
        <CustomH className={styles.userSalutation}>
          {didResetPassword ? (
            <FormattedMessage {...messages.postRecoveryTitle} />
          ) : (
            <>
              <FormattedMessage {...messages.salutationText} />
              <br />
              <FormattedMessage
                {...getSalutationKey(gender)}
                values={{
                  lastName,
                  firstName,
                }}
              />
            </>
          )}
        </CustomH>
      </div>
      <form
        onSubmit={handleSubmit}
        method={'POST'}
        data-qa={'malt-login-known-user-form'}
      >
        <PasswordField
          autoFocus={false}
          label={<FormattedMessage {...messages.passwordLabel} />}
          inputRef={passwordRef}
          handleChange={handleChange}
        />

        <div className={styles.inputActions}>
          <TextButton
            onClick={() => forgotPasswordAction(email)}
            data-qa={'malt-login-forgot-password-link'}
            type={'button'}
          >
            <FormattedMessage {...messages.forgotPasswordLabel} />
          </TextButton>
        </div>

        {errors.general ? (
          <FormMessage
            error={<FormattedMessage {...messages.loginRequestError} />}
          />
        ) : (
          <FormMessage notice={notice} error={errors.credentials} />
        )}

        <StretchButtonGroup>
          <PrimaryButton
            data-qa={'malt-login-known-user-submit'}
            type={'submit'}
            loading={isSubmitting}
          >
            <FormattedMessage {...messages.buttonLabel} />
          </PrimaryButton>
          <TextButton
            onClick={oldLoginAction}
            data-qa={'malt-login-button-not-me'}
          >
            <FormattedMessage {...messages.notYouLink} />
          </TextButton>
        </StretchButtonGroup>
        {breakpoint !== BREAKPOINTS.handheld && (
          <>
            <Spacer size={6} />
            <div className={styles.alignCenterContainer}>
              <H level={4}>
                <FormattedMessage {...messages.oneTimeLinkTeaser} />
              </H>
              <CustomP>
                <FormattedMessage {...messages.oneTimeLinkExplainer} />
              </CustomP>
              <Spacer size={2} />
              {oneTimeLinkError && (
                <FormMessage
                  error={
                    <FormattedMessage {...messages.oneTimeLinkLimitReached} />
                  }
                />
              )}

              <SecondaryButton onClick={oneTimeLinkAction}>
                <FormattedMessage {...messages.oneTimeLinkLink} />
              </SecondaryButton>
            </div>

            <Spacer size={6} />
          </>
        )}
      </form>
    </>
  );
};

LoginKnownUser.propTypes = {
  handleChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  oldLoginAction: PropTypes.func.isRequired,
  oneTimeLinkAction: PropTypes.func.isRequired,
  forgotPasswordAction: PropTypes.func.isRequired,
  formMessage: PropTypes.object.isRequired,
  oneTimeLinkError: PropTypes.string,
  passwordRef: PropTypes.object,
  isSubmitting: PropTypes.bool,
  user: PropTypes.shape({
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    gender: PropTypes.string,
    imageUrl: PropTypes.string,
    hashedId: PropTypes.integer,
  }),
  errors: PropTypes.object,
  values: PropTypes.object.isRequired,
  didResetPassword: PropTypes.bool,
  setErrors: PropTypes.func.isRequired,
};

export default injectIntl(LoginKnownUser);
